/**
 * Reserved first labels that must not be deploy slugs.
 * Includes Studio preview labels and common apex labels under the parent domain.
 */
export const RESERVED_DEPLOY_SLUGS: ReadonlySet<string> = new Set([
  'mktp',
  'web',
  'scratch',
  'studio',
  'www',
  'mail',
  'sso',
  'api',
  'cdn',
  'ftp',
  'ns',
  'smtp',
  'pop',
  'imap',
]);

export const PORT_PREVIEW_LABEL = /^p[0-9]+$/;

/**
 * Parent public domain for deployed apps.
 * `studio.stimcar.site` → `stimcar.site` (apps at `<slug>.stimcar.site`).
 */
export function deployedAppParentDomain(studioHostname: string): string {
  const host = studioHostname.trim().toLowerCase();
  if (host === '') {
    throw new Error('Studio hostname is required for deployed app URLs');
  }
  const parts = host.split('.').filter((part) => part.length > 0);
  if (parts.length < 3) {
    throw new Error(`Studio hostname must be studio.<domain> (got ${studioHostname})`);
  }
  if (parts[0] !== 'studio') {
    throw new Error(`Studio hostname must start with studio. (got ${studioHostname})`);
  }
  return parts.slice(1).join('.');
}

/**
 * Reject slugs that collide with reserved studio host labels or session preview hosts.
 */
export function assertDeployableScratchSlug(slug: string): void {
  const normalized = slug.trim().toLowerCase();
  if (normalized === '' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
    throw new Error(`Invalid deploy slug: ${slug}`);
  }
  if (RESERVED_DEPLOY_SLUGS.has(normalized) || PORT_PREVIEW_LABEL.test(normalized)) {
    throw new Error(`Reserved deploy slug: ${normalized}`);
  }
}

/**
 * Stable public hostname for a deployed app: `<slug>.<parentDomain>`
 * (e.g. late-kanbans.stimcar.site when Studio is studio.stimcar.site).
 */
export function deployedAppHostname(slug: string, studioHostname: string): string {
  assertDeployableScratchSlug(slug);
  const parent = deployedAppParentDomain(studioHostname);
  return `${slug.trim().toLowerCase()}.${parent}`;
}

/** Normalize user input toward a deployable slug (lowercase, spaces → `-`). */
export function normalizeDeploySlugInput(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function tryAssertDeployableScratchSlug(slug: string): string | undefined {
  try {
    assertDeployableScratchSlug(slug);
    return undefined;
  } catch (reason: unknown) {
    return reason instanceof Error ? reason.message : String(reason);
  }
}

export function deployedAppUrl(hostname: string, protocol: 'http' | 'https' = 'https'): string {
  return `${protocol}://${hostname}`;
}

/** Docker network alias for nginx `deploy-$slug` upstream resolution. */
export function deployedAppDockerAlias(slug: string): string {
  assertDeployableScratchSlug(slug);
  return `deploy-${slug.trim().toLowerCase()}`;
}
